import { Locale } from 'vue-i18n';
import { _Boolean, Product } from '@/API';
import { MenuType } from '@/appsync/graphql/menu';
import {
  GetUserMenusQueryVariables,
  GetUserMenusQuery,
  getUserMenus,
} from '@/appsync/graphql/user';
import {
  graphqlQuery,
  getNameByLang,
  getDescriptionByLang,
  getIcon,
  addParameterToUrl,
} from '@/appsync/service/GraphqlUtils';
import {
  OriginMenu,
  MenuListItem,
  LoginInfo,
  ProductAdditionalQueryMap,
} from '@/types/type';
import { _BooleanEnum2boolean } from '@/utils/CommonUtils';
import { CWS_ROOT_MENU_ID } from '@/utils/consts';

interface SiteMapMenuQueryParam extends LoginInfo {
  language: Locale;
  limit: number;
  nextToken: string | null;
}

const orderBySort = (menu1: OriginMenu, menu2: OriginMenu) => {
  return menu1.sort - menu2.sort;
};

const toMenuListItem = (
  menu: OriginMenu,
  productAdditionalQueryMap?: ProductAdditionalQueryMap
): MenuListItem => {
  const product = menu.product;
  const url = menu.url
    ? addParameterToUrl(menu.url, product, productAdditionalQueryMap)
    : menu.url;
  return {
    ...menu,
    id: menu.menuId,
    menuType: 'menu',
    available: true,
    isFavorite: false,
    product,
    url,
  };
};

const buildChildMenus = (
  targetMenu: MenuListItem,
  allMenus: OriginMenu[],
  productAdditionalQueryMap?: ProductAdditionalQueryMap
) => {
  const childList = [];
  for (let i = allMenus.length - 1; i >= 0; i--) {
    const menu = allMenus[i];
    if (menu.parentMenuId === targetMenu.id) {
      childList.push(menu);
      allMenus.splice(i, 1);
    }
  }

  const isCwsCategoryMenuAndHasNoChild =
    childList.length === 0 &&
    targetMenu.product === Product.CWS &&
    targetMenu.isCategory &&
    targetMenu.id !== CWS_ROOT_MENU_ID;

  if (isCwsCategoryMenuAndHasNoChild) {
    const childMenu: MenuListItem = {
      ...targetMenu,
      isCategory: false,
      icon: 'mdi-note-text',
    };
    targetMenu.children = [childMenu];
    return [];
  } else {
    const result: MenuListItem[] = childList
      .sort(orderBySort)
      .map(menu => toMenuListItem(menu, productAdditionalQueryMap));
    targetMenu.children = result;
    return result;
  }
};

const buildMenuStructure = (
  target: MenuListItem,
  allMenus: OriginMenu[],
  productAdditionalQueryMap?: ProductAdditionalQueryMap
) => {
  const children = buildChildMenus(target, allMenus, productAdditionalQueryMap);
  children.forEach(child =>
    buildMenuStructure(child, allMenus, productAdditionalQueryMap)
  );
};

const convertToOriginMenus = (
  menuList: Array<MenuType>,
  language: Locale
): OriginMenu[] => {
  const menus: OriginMenu[] = menuList.map(
    (menu: MenuType): OriginMenu => {
      const title = getNameByLang(menu.menuNames.items, language);
      const description = getDescriptionByLang(
        menu.menuDescriptions.items,
        language
      );
      const isCategory = _BooleanEnum2boolean(menu.isCategory);
      const icon = getIcon(menu.menuIconFont);
      const canUseFromExtra = _BooleanEnum2boolean(menu.canUseFromExtra);
      return { ...menu, title, description, isCategory, icon, canUseFromExtra };
    }
  );
  return menus;
};

const fetchMenus = async (
  param: SiteMapMenuQueryParam
): Promise<OriginMenu[]> => {
  const result = await graphqlQuery<
    GetUserMenusQuery,
    GetUserMenusQueryVariables
  >(getUserMenus, { ...param });
  if (
    result &&
    result.getUser &&
    result.getUser.menus &&
    result.getUser.menus.items
  ) {
    const menus = convertToOriginMenus(
      result.getUser.menus.items,
      param.language
    );
    if (result.getUser.menus.nextToken) {
      const nextMenus = await fetchMenus({
        ...param,
        nextToken: result.getUser.menus.nextToken,
      });
      menus.push(...nextMenus);
    }
    return menus;
  }
  return [];
};

const getQueryParam = (
  loginInfo: LoginInfo,
  language: Locale
): SiteMapMenuQueryParam => {
  return { ...loginInfo, language, limit: 500, nextToken: null };
};

export const getRoot = (): MenuListItem => {
  return {
    id: 'root',
    title: '',
    menuType: 'menu',
    product: Product.CJK,
    description: '',
    available: true,
    isFavorite: false,
    isCategory: true,
    children: [],
    icon: 'mdi-briefcase',
    canUseFromExtra: true,
  };
};

export const fetchMenuList = async (
  loginInfo: LoginInfo,
  language: Locale,
  productAdditionalQueryMap?: ProductAdditionalQueryMap
): Promise<MenuListItem> => {
  const param: SiteMapMenuQueryParam = getQueryParam(loginInfo, language);
  const allMenus: OriginMenu[] = await fetchMenus(param);
  const root: MenuListItem = getRoot();
  buildMenuStructure(root, allMenus, productAdditionalQueryMap);
  if (root.children) {
    root.children = root.children.filter(
      child => child.children && child.children.length
    );
  }
  return root;
};
